import React from 'react';
import { TicketStatusPill } from './TicketStatusPill';
import { TicketPriorityPill } from './TicketPriorityPill';
import { RelativeTime } from './RelativeTime';
import { BRAND, CATEGORY_LABELS, SOURCE_LABELS } from './statuses';
import type { TicketListItem } from './ticketApi';

interface Props {
  ticket: TicketListItem;
  selected: boolean;
  onSelectToggle: (id: string) => void;
  onOpen: (id: string) => void;
}

export const TicketListRow: React.FC<Props> = ({ ticket, selected, onSelectToggle, onOpen }) => {
  const cat = ticket.category ? CATEGORY_LABELS[ticket.category] : undefined;
  const src = SOURCE_LABELS[ticket.source] || { label: ticket.source, bg: BRAND.surfaceMuted, text: BRAND.textSecondary };
  const requesterLabel = ticket.requester_name || ticket.requester_email || '—';
  const isUnread = ticket.status === 'new';

  return (
    <div
      onClick={() => onOpen(ticket.id)}
      style={{
        display: 'grid',
        gridTemplateColumns: '28px 72px 110px 100px 1fr 150px 100px',
        alignItems: 'center',
        gap: 12,
        padding: '14px 16px',
        borderBottom: `1px solid ${BRAND.borderSoft}`,
        cursor: 'pointer',
        background: selected ? 'var(--surface-green-soft)' : (isUnread ? 'var(--surface-bright)' : BRAND.surface),
        transition: 'background 0.12s',
      }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.background = BRAND.surfaceAlt;
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.background = isUnread ? 'var(--surface-bright)' : BRAND.surface;
      }}
    >
      {/* Checkbox */}
      <div onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onSelectToggle(ticket.id)}
          style={{ cursor: 'pointer', width: 14, height: 14, accentColor: BRAND.green }}
        />
      </div>

      {/* Ticket # */}
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: isUnread ? BRAND.green : BRAND.textTertiary,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        #{ticket.ticket_number}
      </div>

      {/* Status */}
      <div>
        <TicketStatusPill status={ticket.status} size="sm" />
      </div>

      {/* Priority */}
      <div>
        <TicketPriorityPill priority={ticket.priority} size="sm" />
      </div>

      {/* Subject + preview */}
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: isUnread ? 700 : 600,
            color: BRAND.text,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginBottom: 2,
          }}
        >
          {cat && (
            <cat.icon size={13} style={{ marginRight: 6, verticalAlign: 'middle', flexShrink: 0 }} />
          )}
          {ticket.subject || '(no subject)'}
          {ticket.message_count > 1 && (
            <span
              style={{
                marginLeft: 8,
                fontSize: 11,
                fontWeight: 600,
                color: BRAND.textTertiary,
                background: BRAND.surfaceMuted,
                padding: '1px 6px',
                borderRadius: 100,
                verticalAlign: 'middle',
              }}
            >
              {ticket.message_count}
            </span>
          )}
          {ticket.roadmap_item_id && (
            <span
              title="Promoted to roadmap"
              style={{
                marginLeft: 8,
                fontSize: 10,
                fontWeight: 700,
                color: BRAND.green,
                background: 'rgba(31,108,79,0.10)',
                padding: '2px 7px',
                borderRadius: 100,
                verticalAlign: 'middle',
                letterSpacing: '0.03em',
                textTransform: 'uppercase',
              }}
            >
              Promoted
            </span>
          )}
        </div>
        <div
          style={{
            fontSize: 12,
            color: BRAND.textTertiary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {ticket.last_message_preview || '—'}
        </div>
      </div>

      {/* Requester + source */}
      <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            display: 'inline-block',
            padding: '2px 7px',
            borderRadius: 100,
            fontSize: 10,
            fontWeight: 700,
            background: src.bg,
            color: src.text,
            flexShrink: 0,
          }}
        >
          {src.label}
        </span>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              color: BRAND.textSecondary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontWeight: 500,
            }}
          >
            {requesterLabel}
          </div>
        </div>
      </div>

      {/* Updated time */}
      <div
        style={{
          fontSize: 12,
          color: BRAND.textTertiary,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <RelativeTime iso={ticket.updated_at} />
      </div>
    </div>
  );
};
